<template>
  <div>
    <div
      v-if="showStar && !props.clickable"
      class="questionRewardAnimationContainer"
      :class="{ target: goToTarget }"
      ref="questionRewardAnimationContainer"
    >
      <font-awesome-icon
        :icon="faStar"
        size="4x"
        :spin="true"
        style="color: #ffd43b"
        class="fast-spin no-select"
      />
    </div>

    <MButton
      v-if="props.clickable"
      tag="div"
      class="rewardBackdrop"
      @click="onInteraction()"
      :useDefaultStyles="false"
    />

    <div v-if="props.clickable" class="rewardCardWrapper">
      <div :class="['imageRewardCard', { showCard, target: goToTarget }]">
        <div class="starImageContainer">
          <div class="starImage">
            <img
              v-if="props.clickable"
              src="@/assets/images/star.png"
              alt=""
              class="no-select slow-spin"
            />
          </div>
        </div>

        <p class="hint">{{ customMessage || 'Tap the star to collect it!' }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeMount, onBeforeUnmount, computed } from 'vue'
import type { Ref } from 'vue'

import MButton from '@/components/MButton.vue'
// Stores
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { useActivityStore } from '@/stores/ActivityStore'
import { useDataStore } from '@/stores/DataStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'

//Services
import FullScreen from '@/services/FullScreen.js'

// Types
import type { AudioPlayer, AudioLoadError, ManagedTimeout, progressSateError } from '@/customTypes'

// Icons
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faStar } from '@fortawesome/free-solid-svg-icons'

// Helpers
import { useTimeout } from '@/utils/helpers'

const emit = defineEmits<{
  (e: 'reward-collected'): void
}>()

const props = defineProps({
  questionReward: {
    type: String,
    required: true
  },
  clickable: {
    type: Boolean,
    default: false
  },
  firstLoad: {
    type: Boolean,
    default: false
  },
  customAudioUrl: {
    type: String,
    default: ''
  },
  customMessage: {
    type: String,
    default: ''
  },
  addStarOnInteraction: {
    type: Boolean,
    default: true
  }
})

const soundEffectsStore = useSoundEffectsStore()
const dataStore = useDataStore()
const activityStore = useActivityStore()
const activeScreenerStore = useActiveScreenerStore()
const { setManagedTimeout, clearManagedTimeout } = useTimeout()

const userResponseTimeout = ref<ManagedTimeout | null>(null)

const firstLoadVoiceUrl: string = `${_ASSETS_BUCKET_URL_}/${dataStore.screenerTemplateId}/avatar-audio/CLICKABLE_REWARD_FIRST_LOAD_VOICE.mp3?v=${_ASSETS_VERSION_}`
const voiceUrl: string = `${_ASSETS_BUCKET_URL_}/${dataStore.screenerTemplateId}/avatar-audio/CLICKABLE_REWARD_VOICE.mp3?v=${_ASSETS_VERSION_}`

const transitionDuration: number = 400
const questionRewardAnimationContainer = ref<HTMLElement | null>(null)
const randomStartPosition: Ref<{ x: string; y: string }> = ref({ x: '500px', y: '0px' })
const counterDestination: Ref<{ x: number; y: number }> = ref({ x: 0, y: 0 })

const audioPlayer: Ref<AudioPlayer | null> = ref(null)

const showCard: Ref<boolean> = ref(false)
const goToTarget: Ref<boolean> = ref(false)
const showStar: Ref<boolean> = ref(true)

const starGathered: Ref<boolean> = ref(false)

const userUnresponsive: Ref<boolean> = ref(false)

const COMPONENT_ERROR_TYPE: progressSateError = 'UserUnresponsive'

onBeforeMount(async () => {
  if (!props.clickable) {
    setRandomInitialPosition()
  }

  try {
    if (props.firstLoad) {
      audioPlayer.value = await soundEffectsStore.createPlayerForTrack({
        id: 'firstLoadRewardVoice',
        name: 'firstLoadRewardVoice',
        url: firstLoadVoiceUrl,
        volume: dataStore.avatarVolumeLevel,
        loop: false
      })
    } else {
      const id = props.customAudioUrl.split('/').pop()?.split('.')[0] || 'rewardVoice'
      const name = id
      const url = props.customAudioUrl || voiceUrl

      audioPlayer.value = await soundEffectsStore.createPlayerForTrack({
        id,
        name,
        url,
        volume: dataStore.avatarVolumeLevel,
        loop: false
      })
    }
  } catch (e) {
    if ((e as AudioLoadError).name === 'AudioLoadError') {
      const error = e as AudioLoadError
      soundEffectsStore.startLoadErrorTimeout(error.error, error.id)
    }
  }
})

onMounted(() => {
  //on resize update the star bag position
  window.onresize = getCounterDestination

  getCounterDestination()
  startAnimation()
})

onBeforeUnmount(() => {
  if (audioPlayer.value) {
    audioPlayer.value.end()
  }
  clearManagedTimeout(userResponseTimeout)

  audioPlayer.value?.remove()
  audioPlayer.value = null
  window.onresize = null
})

const screenerHasComponentError = computed(() =>
  activeScreenerStore.hasErrorState(COMPONENT_ERROR_TYPE)
)

// Multiple click prevention
const onInteraction = (): void => {
  if (!FullScreen.isEnabled()) {
    FullScreen.toggleAll()
  }

  if (starGathered.value) return
  clearManagedTimeout(userResponseTimeout)

  if (userUnresponsive.value || screenerHasComponentError.value) {
    userUnresponsive.value = false
    sendResponsiveNotification()
  }

  starGathered.value = true
  onStarClick()
}

const playCollectionSound = () => {
  if (props.questionReward) {
    const collectionSound = props.customAudioUrl
      ? props.questionReward.split('/').pop()?.split('.')[0]
      : props.questionReward.split('.')[0]
    soundEffectsStore.playTrack(collectionSound || '')
  }
}

const playAvatarVoice = () => {
  if (audioPlayer.value) {
    audioPlayer.value.start()
  }
}
/**
 * Get the center of the reward counter
 */
function getCounterDestination() {
  const counterElement: HTMLElement | null = document.getElementsByClassName(
    'starCounterAnimationHolder'
  )?.[0] as HTMLElement
  if (!counterElement) return
  const rect = counterElement.getBoundingClientRect()

  // Check the orientation and calculate the position accordingly
  if (window.innerWidth > window.innerHeight) {
    const positionFromLeft = window.innerWidth - rect.left - counterElement.offsetWidth / 2
    const positionFromTop = ((counterElement.offsetHeight + rect.bottom) / 2) * -1
    counterDestination.value.x = positionFromLeft
    counterDestination.value.y = positionFromTop
  } else {
    const positionFromLeft = rect.left - window.innerWidth
    const positionFromTop = window.innerHeight - rect.top - counterElement.offsetWidth / 2
    counterDestination.value.x = positionFromTop
    counterDestination.value.y = positionFromLeft
  }
}

/**
 * Set random start position for the star
 */
const setRandomInitialPosition = () => {
  const appComponent: HTMLElement | null = document.querySelector('#app') as HTMLElement

  if (!appComponent) return

  const rect = appComponent.getBoundingClientRect()

  const randomX = Math.floor(Math.random() * rect.width)
  randomStartPosition.value.x = `${randomX}`
  randomStartPosition.value.y = `${rect.height * 1.2}`
}

/**
 * Start the animation
 */
const startAnimation = () => {
  if (props.clickable) {
    bigStarAnimation()
  } else {
    smallStarAnimation()
  }
}

const bigStarAnimation = () => {
  showCard.value = true
  userResponseTimeout.value = setManagedTimeout(() => {
    sendUnresponsiveNotification()
  }, 15000) // 15 seconds

  setManagedTimeout(() => {
    playAvatarVoice()
  }, transitionDuration)
}

const smallStarAnimation = () => {
  playCollectionSound()
  goToTarget.value = true

  setManagedTimeout(() => {
    dataStore.addStar()
    showStar.value = false
    emit('reward-collected')
  }, transitionDuration)
}

const onStarClick = () => {
  goToTarget.value = true
  playCollectionSound()
  activityStore.updateActivity()
  setManagedTimeout(() => {
    showCard.value = false
    if (props.addStarOnInteraction) {
      dataStore.addStar()
    }
    showStar.value = false
    emit('reward-collected')
  }, transitionDuration)
}

function sendUnresponsiveNotification() {
  userUnresponsive.value = true
  activeScreenerStore.sendScreenerError({
    type: COMPONENT_ERROR_TYPE,
    message:
      'The user may need help proceeding with the screener, their response timing is longer than expected.',
    priority: 1
  })
}

function sendResponsiveNotification() {
  activeScreenerStore.sendScreenerResolvedError({
    type: COMPONENT_ERROR_TYPE
  })
}
</script>

<style lang="scss" scoped>
.questionRewardAnimationContainer {
  position: fixed;
  top: v-bind('`${randomStartPosition.y}px`');
  left: v-bind('`${randomStartPosition.x}px`');

  transform: translate(-50%, -50%);
  transition: all v-bind('`${transitionDuration}ms`') linear;

  &.target {
    top: calc(v-bind('`${counterDestination.y}px`'));
    left: calc(var(--full-vw) - v-bind('`${counterDestination.x}px`'));
  }
  .fast-spin {
    @include animation(fa-spin 0.75s infinite linear);
  }
}

.rewardCardWrapper {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  width: 100;
  pointer-events: none;
  font-family: 'Lilita One', sans-serif;
  font-weight: 400;
  font-style: normal;
  text-wrap: nowrap;
  font-size: 2rem;
  text-shadow: 2px 2px 2px rgba(0, 0, 0, 0.1);
  z-index: 1001;

  .imageRewardCard {
    padding: 2rem;
    border-radius: 2rem;
    box-shadow: 0px 0px 15px 10px rgba(255, 215, 18, 0.6);
    background-color: $light;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    @include prefixed(transform-origin, center);
    scale: 0;
    transition: scale v-bind('`${transitionDuration}ms`') ease-in-out;
    pointer-events: none;

    &.showCard {
      scale: 1;
    }

    .starImageContainer {
      width: min(calc(var(--full-vw) * 0.3), 500px);
      height: min(calc(var(--full-vw) * 0.3), 600px);
      margin: 2rem;
    }

    .starImage img {
      width: 100%;
      transform: translate(0px, 0px);
    }

    &.target .starImage img {
      @include animation(fa-spin 0.75s infinite linear);
    }
    &.target .starImage {
      @include animation(flyOff v-bind('`${transitionDuration}ms`') forwards);
    }

    @include keyframes(flyOff) {
      0% {
        transform: translate3d(0px, 0px, 0px);
      }
      100% {
        transform: translate3d(
            calc(var(--full-vw) * 0.5 - v-bind('`${counterDestination.x}px`')),
            calc((var(--full-vh) * -0.5) - v-bind('`${counterDestination.y}px`')),
            0px
          )
          scale(0.1);
      }
    }

    @include keyframes(growStar) {
      0%,
      50% {
        transform: scale(0);
      }
      100% {
        transform: scale(0.8);
      }
    }
  }
}

.rewardBackdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: var(--full-vw);
  height: var(--full-vh);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  touch-action: manipulation;
  background-color: rgba(0, 0, 0, 0);
  border: none;
}

.slow-spin {
  @include animation(fa-spin 3s infinite linear);
}
</style>
